import { CardProWiringProviderInterface } from "@totalenergiescode/mobility-business-rev-form-data/components";
import { useTranslation } from "react-i18next";

import { prettifyJson } from "@/components/FormData/shared/json";
import PreviewTemplate from "@/components/FormData/templates/PreviewTemplate/component";
import { StyledForm } from "@/components/formDataInjector/styles";

import { AccountSearch } from "./AccountSearch/component";
import { DivisionSelector } from "./DivisionSelector/component";
import { CreationErrorBanner } from "./ErrorBanner/component";
import { GetTemplateButton } from "./GetTemplateButton/component";
import { useCreationTemplate } from "./hooks";
import { LedgerTypeSelector } from "./LedgerTypeSelector/component";
import {
  StyledDrawer,
  StyledDrawerInnerContainer,
  StyledHeaderText,
  StyledRoundedTopLeftContainer,
  StyledSection,
} from "./styles";

const CreationTemplate = ({
  isOpen,
  onClose,
  provider,
  onTemplateSelected,
}: {
  isOpen: boolean;
  onClose: () => void;
  provider: CardProWiringProviderInterface;
  onTemplateSelected: (json: string) => void;
}) => {
  const { t: tFormData } = useTranslation("formData");
  const {
    form: { control },
    walletId,
    ledgerTypeId,
    selectedAccount,
    accountError,
    divisionOptions,
    ledgerTypeOptions,
    creationTemplateError,
    creationTemplateData,
    handleSearchAccount,
    handleSelectWallet,
    handleSelectLedgerType,
    handleFetchCreationTemplate,
    canFetchCreationTemplate,
    isAccountLoading,
    isCreationTemplateLoading,
  } = useCreationTemplate({
    provider,
    tFormData,
  });

  const divisionLabel =
    divisionOptions.find((option) => option.value === walletId)?.label ?? "";
  const ledgerTypeLabel =
    ledgerTypeOptions.find((option) => option.value === ledgerTypeId)?.label ??
    "";
  const accountLabel =
    selectedAccount &&
    tFormData("formData.creationTemplate.selectedAccountDetails", {
      accountNumber:
        selectedAccount.number ?? selectedAccount.accountId ?? walletId,
      accountName: selectedAccount.name ?? selectedAccount.label,
    });

  function handleLoadTemplate() {
    if (!creationTemplateData) {
      return;
    }

    onTemplateSelected(
      prettifyJson(JSON.parse(creationTemplateData.rawFormData)),
    );
    onClose();
  }

  return (
    <StyledDrawer
      dataTestId={"formdata__create-template-drawer"}
      isOpen={isOpen}
      onClose={onClose}
    >
      <StyledHeaderText variant='title5'>
        {tFormData("formData.creationTemplate.title")}
      </StyledHeaderText>

      <StyledRoundedTopLeftContainer>
        <StyledDrawerInnerContainer>
          <StyledForm>
            <AccountSearch
              accountError={accountError}
              control={control}
              isAccountLoading={isAccountLoading}
              onSearchAccount={handleSearchAccount}
              selectedAccount={selectedAccount}
              tFormData={tFormData}
            />

            {selectedAccount && (
              <DivisionSelector
                control={control}
                divisionOptions={divisionOptions}
                onSelectWallet={handleSelectWallet}
                tFormData={tFormData}
              />
            )}

            {selectedAccount && walletId && (
              <LedgerTypeSelector
                control={control}
                ledgerTypeOptions={ledgerTypeOptions}
                onSelectLedgerType={handleSelectLedgerType}
                tFormData={tFormData}
              />
            )}

            {selectedAccount && walletId && ledgerTypeId && (
              <StyledSection>
                <GetTemplateButton
                  canFetchCreationTemplate={canFetchCreationTemplate}
                  isCreationTemplateLoading={isCreationTemplateLoading}
                  onFetchCreationTemplate={handleFetchCreationTemplate}
                  tFormData={tFormData}
                />
                {creationTemplateError && (
                  <CreationErrorBanner
                    errorMessage={creationTemplateError}
                    tFormData={tFormData}
                  />
                )}

                {creationTemplateData && (
                  <PreviewTemplate
                    variant='creation'
                    templateData={creationTemplateData}
                    accountSummary={{
                      accountLabel: accountLabel ?? "",
                      divisionLabel,
                      ledgerTypeLabel,
                    }}
                    onLoad={handleLoadTemplate}
                  />
                )}
              </StyledSection>
            )}
          </StyledForm>
        </StyledDrawerInnerContainer>
      </StyledRoundedTopLeftContainer>
    </StyledDrawer>
  );
};

export default CreationTemplate;
